import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtUSD, fmtNum, apiError } from "../../lib/api";
import { countdownFor, daysLeft } from "../../lib/dealMeta";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState(null);
  const [joined, setJoined] = useState([]);
  const [closing, setClosing] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/portfolio").then((r) => setData(r.data)).catch(() => setData({ holdings: [], summary: {} }));
    api.get("/my-terms").then((r) => setTerms(r.data)).catch(() => {});
    api.get("/referral/joined").then((r) => setJoined(r.data)).catch(() => {});
    api.get("/deals").then((r) => setClosing((r.data || []).filter((d) => {
      const n = daysLeft(d.closes_date);
      return d.status === "open" && n != null && n >= 0 && n <= 7;
    }))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportStatement = async () => {
    try {
      const res = await api.get("/portfolio/statement.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "solera-portfolio-statement.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyReferral = async () => {
    try {
      const { data } = await api.get("/referral");
      await navigator.clipboard.writeText(`${window.location.origin}/register?ref=${data.code}`);
      toast.success("Referral link copied. It names you as the referrer to the person you send it to.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <span className="t-label">Loading</span>;
  const { holdings, summary } = data;

  return (
    <div data-testid="portfolio-page" className="rise">
      {terms?.groups?.length > 0 && (
        <div className="mb-12 p-6 rise" style={{ background: "#14171A", border: "1px solid #24272B" }} data-testid="terms-card">
          <span className="t-label">Your negotiated terms</span>
          <p className="t-small text-muted-s mt-2 measure">
            Through {terms.groups.map((g) => g.name).join(", ")}. These apply automatically to your commitments.
          </p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
            {terms.effective.finder_fee_percent != null && (
              <div><span className="t-label">Finders fee</span><p className="fig text-bone mt-1">{terms.effective.finder_fee_percent}%</p></div>
            )}
            {terms.effective.min_commitment_usd != null && (
              <div><span className="t-label">Minimum</span><p className="fig text-bone mt-1">{fmtUSD(terms.effective.min_commitment_usd)}</p></div>
            )}
            {terms.effective.mgmt_fee_discount_percent != null && (
              <div><span className="t-label">Mgmt fee discount</span><p className="fig text-bone mt-1">{terms.effective.mgmt_fee_discount_percent}%</p></div>
            )}
            {terms.effective.carry_discount_percent != null && (
              <div><span className="t-label">Carry discount</span><p className="fig text-bone mt-1">{terms.effective.carry_discount_percent}%</p></div>
            )}
            {terms.effective.priority_allocation && (
              <div><span className="t-label">Allocation</span><p className="t-small text-bone mt-1">Priority</p></div>
            )}
          </div>
          {terms.effective.notes?.length > 0 && (
            <p className="t-small text-muted-s mt-4 measure">{terms.effective.notes.map((n) => n.note).join(" ")}</p>
          )}
        </div>
      )}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <span className="t-label">Portfolio</span>
          <h1 className="t-h1 text-bone mt-3">
            Entity: {user?.preferences?.entity_name || user?.name}
          </h1>
        </div>
        <button onClick={exportStatement} className="btn-ghost" data-testid="export-portfolio-button">
          Export statement
        </button>
      </div>

      {closing.length > 0 && (
        <div className="mt-10 p-6 rise" style={{ background: "#14171A", border: "1px solid #24272B" }} data-testid="closing-soon-card">
          <span className="t-label">Closing soon</span>
          <div className="mt-3 space-y-0">
            {closing.map((d) => {
              const cd = countdownFor(d.status, d.closes_date, d.closes_at);
              return (
                <Link key={d.deal_id} to={`/app/deals/${d.deal_id}`}
                  className="hairline first:border-t-0 py-3 flex flex-wrap gap-x-8 gap-y-1 items-baseline justify-between group"
                  data-testid={`closing-soon-${d.deal_id}`}>
                  <span className="t-body text-bone group-hover:underline underline-offset-4">{d.company}</span>
                  <span className="t-cap text-muted-s flex-1">{d.round_label}</span>
                  <span className="t-label text-bone">{cd.text}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-10">
        {[
          ["Positions", fmtNum(summary.positions)],
          ["Cost", fmtUSD(summary.cost)],
          ["Value at mark", fmtUSD(summary.value)],
          ["MOIC", summary.moic ? summary.moic.toFixed(2) : "0.00"],
          ["Distributions", fmtUSD(summary.distributions)],
          ["DPI", summary.dpi ? summary.dpi.toFixed(2) : "0.00"],
        ].map(([label, val]) => (
          <div key={label} className="hairline pt-4">
            <span className="t-label">{label}</span>
            <div className="fig text-bone text-2xl mt-2" data-testid={`summary-${label.toLowerCase().replace(/ /g, "-")}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="mt-14 overflow-x-auto">
        <table className="table-solera" data-testid="holdings-table">
          <thead>
            <tr>
              <th>Position</th><th>Class</th>
              <th className="num">Units</th><th className="num">Entry</th>
              <th className="num">Mark</th><th className="num">MOIC</th>
              <th>Distributions</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h) => (
              <tr key={h.holding_id} data-testid={`holding-row-${h.holding_id}`}>
                <td className="text-bone">{h.company}</td>
                <td className="text-muted-s">{h.share_class}</td>
                <td className="num text-bone">{fmtNum(h.units)}</td>
                <td className="num text-bone">{h.entry_price.toFixed(2)}</td>
                <td className="num text-bone">{h.current_mark.toFixed(2)}</td>
                <td className="num text-bone">{(h.current_mark / h.entry_price).toFixed(2)}</td>
                <td className="text-muted-s t-small">
                  {h.distributions?.length
                    ? h.distributions.map((d) => `${d.tranche}, ${d.date}`).join("; ")
                    : "none"}
                </td>
              </tr>
            ))}
            {holdings.length === 0 && (
              <tr><td colSpan={7} className="text-muted-s t-small py-8">
                No positions yet. Positions appear here once a transfer is recognized on the issuer's books.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="t-cap text-muted-s mt-6">
        Marks as at 1 June 2026. Distributions are shown as tranches with dates, never as a single realized number.
      </p>

      <div className="hairline mt-12 pt-6 flex items-baseline justify-between gap-6 flex-wrap" data-testid="referral-block">
        <div>
          <span className="t-label">Refer a member</span>
          <p className="t-small text-muted-s mt-2 measure">
            Membership grows one referral at a time. Your link names you as the referrer, and nothing else.
          </p>
        </div>
        <button onClick={copyReferral} className="btn-ghost" data-testid="copy-referral-button">Copy your referral link</button>
      </div>
      {joined.length > 0 && (
        <div className="mt-6" data-testid="referral-joined-list">
          <span className="t-label">Your referrals, {joined.length} joined</span>
          <div className="mt-2 space-y-0">
            {joined.map((j, i) => (
              <div key={`${j.name}-${j.created_at}-${i}`} className="hairline py-3 flex gap-8 items-baseline">
                <span className="t-small text-bone">{j.name}</span>
                <span className="t-cap text-muted-s fig">Joined {j.created_at?.slice(0, 10)}</span>
                <span className="t-cap text-muted-s">{j.onboarded ? "Onboarded" : "Onboarding pending"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
